// src/components/reader/CodeBlock.js
import React, { useEffect, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { createHighlighter } from 'shiki';

export default function CodeBlock({ language, value }) {
    const [html, setHtml] = useState('');
    const [copied, setCopied] = useState(false);
    const lineCount = value.split('\n').length;

    useEffect(() => {
        let cancelled = false;
        createHighlighter({ themes: ['light-plus'], langs: [language] })
            .then(highlighter => {
                try {
                    if (cancelled) return;
                    // The rendered HTML does not need to retain the grammar engine.
                    let codeHtml = highlighter.codeToHtml(value, { lang: language, theme: 'light-plus' });
                    codeHtml = codeHtml.replace(/background-color:[^;"]+;?/g, 'background-color: transparent;');
                    setHtml(codeHtml);
                } finally {
                    // Also dispose when initialization finishes after unmount.
                    highlighter.dispose();
                }
            })
            .catch(error => {
                if (!cancelled) console.error('Code highlighting failed', error);
            });
        return () => { cancelled = true; };
    }, [language, value]);

    useEffect(() => {
        if (!copied) return undefined;
        const timer = setTimeout(() => setCopied(false), 2000);
        return () => clearTimeout(timer);
    }, [copied]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
        } catch (err) {
            console.error('Copy failed', err);
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            {lineCount > 3 && (
                <Tooltip title={copied ? 'Copied!' : 'Copy code to clipboard'}>
                    <IconButton
                        onClick={handleCopy}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: '0.5rem',
                            right: '0.5rem',
                            borderRadius: 1,
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.2)' }
                        }}
                    >
                        {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                    </IconButton>
                </Tooltip>
            )}

            <div
                className="code-block"
                style={{
                    background: '#f5f5f5',
                    padding: '1em',
                    borderRadius: '8px',
                    fontSize: '0.9rem',
                    overflowX: 'auto',
                    marginTop: '0.5em',
                    paddingTop: '0',
                    paddingBottom: '0',
                }}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </div>
    );
}
